<template>
  <el-dialog
    v-model="isDialogVisible"
    title="Create Order"
    width="500"
    align-center
    :close-on-click-modal="false"
    @close="handleDialogClose"
  >
    <v-card-text>
      <div>
        <v-label>Order ID or Merchant Order ID </v-label>
        <v-text-field
          v-model="inputOrderId"
          density="comfortable"
          required
          hide-details="auto"
          variant="outlined"
          color="primary"
          @keyup.enter="submitPayment"
        />
      </div>

      <div class="mt-2">
        <v-label>Please enter an amount</v-label>
        <v-text-field
          v-model="inputAmount"
          placeholder="0 - 200,000"
          density="comfortable"
          required
          hide-details="auto"
          variant="outlined"
          color="primary"
          maxlength="10"
          @input="
            inputAmount = inputAmount
              .replace(/[^0-9.]/g, '')
              .replace(/(\..*)\./g, '$1')
              .slice(0, 10)
          "
          :rules="[
            (v) => !!v || 'Amount is required',
            (v) => {
              const numberValue = parseFloat(v);
              return (
                (numberValue >= 0 && numberValue <= 200000) ||
                'Amount must be between 0 and 200,000'
              );
            },
            (v) =>
              /^\d+(\.\d{1,2})?$/.test(v) ||
              'Amount must be a valid decimal with up to two decimal places',
          ]"
          @keyup.enter="submitPayment"
        />
      </div>

      <v-label class="mt-2 mr-2">Choose Provider</v-label>

      <el-radio-group v-model="selectedProviderMethod">
        <el-radio
          v-for="method in providerMethods"
          :key="method.value"
          :label="method.value"
        >
          {{ method.label }}
        </el-radio>
      </el-radio-group>

      <div class="mt-2">
        <v-label>Please enter a remark</v-label>
        <v-text-field
          v-model="inputRemark"
          placeholder="Remark"
          class="mt-2"
          density="comfortable"
          hide-details="auto"
          variant="outlined"
          color="primary"
          @keyup.enter="submitPayment"
          maxlength="50"
        />
      </div>

      <div class="mt-4">
        <v-label>Bank</v-label>

        <el-select
          v-model="dialogData.bank"
          placeholder="Please select a bank"
          size="large"
          class="v-input__control"
          :rules="bankRules"
          required
        >
          <el-option
            v-for="bank in banks"
            :key="bank.value"
            :label="bank.label"
            :value="bank.value"
          />
        </el-select>
      </div>

      <div class="mt-4">
        <v-label>Account Number</v-label>
        <v-text-field
          aria-label="account_number"
          v-model="dialogData.account_number"
          maxlength="12"
          class="mt-2"
          density="comfortable"
          variant="outlined"
          color="primary"
          :rules="accountNumberRules"
          required
        ></v-text-field>
      </div>

      <v-row>
        <v-col cols="6">
          <v-label>First Name</v-label>
          <v-text-field
            aria-label="first_name"
            v-model="dialogData.first_name"
            class="mt-2"
            density="comfortable"
            variant="outlined"
            color="primary"
            :rules="fnameRules"
            required
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-label>Last Name</v-label>
          <v-text-field
            aria-label="last_name"
            v-model="dialogData.last_name"
            class="mt-2"
            density="comfortable"
            variant="outlined"
            color="primary"
            :rules="lnameRules"
            required
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-text>

    <template #footer>
      <div class="dialog-footer">
        <el-button
          variant="flat"
          size="large"
          rounded="md"
          type="danger"
          @click="resetFields()"
          >Cancel</el-button
        >
        <el-button
          variant="flat"
          size="large"
          rounded="md"
          type="primary"
          @click="submitPayment()"
          >Create
        </el-button>
      </div>
    </template>
  </el-dialog>

  <payment-detail v-model="isDialogPayment" :selectedOrder="selectedOrder" />
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  inject,
  watch,
  defineExpose,
  defineProps,
  defineEmits,
  nextTick,
} from "vue";
import { remarkForApi } from "@/core/helpers/remark";
import ApiService from "@/core/services/Transaction.ts";
import ApiAccountService from "@/core/services/Account.ts";
import { LoadingService } from "@/core/plugins/loading";
import { ElNotification } from "element-plus";
import SvgSprite from "@/components/shared/SvgSprite.vue";

import { useRoute, useRouter } from "vue-router";

import PaymentDetail from "@/views/sellstock/transaction/PayDetail";

const route = useRoute();
const router = useRouter();

const $loading = inject("$loading") as LoadingService;

const selectedProviderMethod = ref("");

const providerMethods = ref<{ label: string; value: string }[]>([]);

const loadDepositProviders = async () => {
  const resp = await ApiAccountService.depositProviders();
  const codes = Array.isArray(resp?.data) ? resp.data : [];
  providerMethods.value = codes
    .filter((code: string) => typeof code === "string" && code.trim() !== "")
    .map((code: string) => {
      const value = code.trim().toLowerCase();
      return { label: value.toUpperCase(), value };
    });
  const stillValid = providerMethods.value.some(
    (method) => method.value === selectedProviderMethod.value
  );
  if (!stillValid) {
    selectedProviderMethod.value = providerMethods.value[0]?.value || "";
  }
};

const userAccounts = ref("");

const inputAmount = ref("");
const inputRemark = ref("");
const inputOrderId = ref("");

const amountInput = ref<HTMLInputElement | null>(null);

const isDialogPayment = ref(false);
const selectedOrder = ref(null);

const props = defineProps({
  modelValue: Boolean,
});

const dialogData = ref({
  bank: "",
  account_number: "",
  first_name: "",
  last_name: "",
});

interface BankOption {
  value: string;
  label: string;
}

const banks: BankOption[] = [
  { value: "bbl", label: "Bangkok Bank" },
  {
    value: "baac",
    label: "Bank for Agriculture and Agricultural Cooperatives",
  },
  { value: "bay", label: "Bank of Ayudhya" },
  { value: "cimb", label: "CIMB Thai Bank" },
  { value: "gov", label: "Government Savings Bank" }, // gov
  { value: "kbank", label: "Kasikorn Bank" },
  { value: "kk", label: "Kiatnakin Phatra Bank" }, // kk
  { value: "ktb", label: "KTB" },
  { value: "scb", label: "SCB" },
  { value: "ttb", label: "TMB Thanachart Bank" },
  { value: "uobt", label: "United Overseas Bank (Thai)" },
];

const emit = defineEmits();

const isDialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
  },
});

const resetFields = () => {
  selectedProviderMethod.value = "";
  inputAmount.value = "";
  inputRemark.value = "";
  inputOrderId.value = "";
  isDialogVisible.value = false;
  dialogData.value = {
    bank: "",
    account_number: "",
    first_name: "",
    last_name: "",
  };
};

const handleDialogClose = () => {
  emit("update:modelValue", false);
  // if (route.path.includes("sellstock/transaction")) {
  //   window.location.reload();
  // }
  resetFields();
};

watch(selectedProviderMethod, (newVal) => {
  if (newVal === 1 || newVal === 2) {
    // if (!userAccounts.value) {
    fetchUserAccounts();
    // }
  }
});

const handlePay = (item: any) => {
  isDialogVisible.value = true;
};

const submitPayment = async (order) => {
  $loading.startLoading();
  if (selectedProviderMethod.value) {
    let payload: any = {
      provider: selectedProviderMethod.value,
    };

    if (inputAmount.value < 0 || inputAmount.value > 200000) {
      ElNotification({
        title: "Error",
        message: "Amount must be between 0 and 200,000",
        type: "error",
      });
      $loading.endLoading();
      return;
    }

    payload.amount = parseFloat(inputAmount.value);
    payload.remark = remarkForApi(inputRemark.value);
    payload.order_id = inputOrderId.value;

    payload.user_bank_account = dialogData.value.bank;
    payload.user_account_number = dialogData.value.account_number;
    payload.user_name =
      dialogData.value.first_name + " " + dialogData.value.last_name;

    const resp = await ApiAccountService.PayWithProvider(payload);
    if (resp.payment) {
      selectedOrder.value = resp.payment;
      await nextTick();
      isDialogPayment.value = true;
      ElNotification({
        title: "Success",
        message: resp.message,
        type: "success",
      });

      resetFields();
    } else {
      ElNotification({
        title: "Error",
        message: resp.error || resp.msg,
        type: "error",
      });
    }
  } else {
    ElNotification({
      title: "Error",
      message: "Please select a Provider.",
      type: "error",
    });
  }
  $loading.endLoading();
};

const closeUploadDialog = () => {
  isDialogPayment.value = false;
  selectedOrder.value = null;
};

const initData = (data: any) => {
  selectedProviderMethod.value = (data.provider || "").toLowerCase();
  inputAmount.value = data.amount ? data.amount.toString() : "";
  inputRemark.value = data.remark || "";
  
  let accNum = data.raw_user_account_number || data.user_account_number || "";
  if (accNum && (accNum.includes("(") || accNum.includes("-"))) {
    const match = accNum.match(/\(([^-]+)-([^)]+)\)/);
    if (match) {
      accNum = match[2];
    }
  }

  inputOrderId.value = data.merchant_order_id || data.order_id || "";

  const names = (data.user_name || "").trim().split(/\s+/);
  dialogData.value = {
    bank: (data.user_bank_account || "").toLowerCase(),
    account_number: accNum,
    first_name: names[0] || "",
    last_name: names.slice(1).join(" ") || "",
  };
};

defineExpose({
  initData,
});

watch(
  () => isDialogVisible.value,
  (newVal) => {
    if (newVal) {
      loadDepositProviders();
      nextTick(() => {
        const inputEl = amountInput.value?.$el?.querySelector("input");
        if (inputEl) {
          inputEl.focus();
        }
      });
    }
  }
);
</script>
